import React, { useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { adminGet, adminPatch, adminPost, formatApiError } from "../../lib/adminApi";
import {
  PageHeader, StatusBadge, ErrorBanner, OnbruikbaarMerkje, OvergeslagenBanner, formatDate,
} from "../../components/admin/AdminUI";
import {
  SIGNAAL_UITLEG, FASE_NAMEN, NIVEAU_UITLEG, signaalZin, splitsVerbeteringen,
  perTweeZinnen, signaalNiveau, uitnodigingRedenNetjes,
} from "../../lib/namen";
import { NotitieVeld } from "../../components/admin/NotitieVeld";
import { WERKNEMER_STATUSES, WERKNEMER_STATUS_LABELS } from "../../lib/constants";

export default function WerknemerDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [statusOpen, setStatusOpen] = useState(false);
  const [avgOpen, setAvgOpen] = useState(false);
  const [opzegOpen, setOpzegOpen] = useState(false);

  const q = useQuery({
    queryKey: ["werknemer", id],
    queryFn: () => adminGet(`/api/admin/werknemers/${id}`),
  });

  const [bewerkOpen, setBewerkOpen] = useState(false);
  const bewerkMut = useMutation({
    mutationFn: (body) => adminPatch(`/api/admin/werknemers/${id}`, body),
    onSuccess: () => {
      setBewerkOpen(false);
      toast.success("Gegevens bijgewerkt");
      qc.invalidateQueries({ queryKey: ["werknemer", id] });
    },
    onError: (e) => toast.error(formatApiError(e)),
  });

  const statusMut = useMutation({
    mutationFn: (body) => adminPost(`/api/admin/werknemers/${id}/status-override`, body),
    onSuccess: () => {
      toast.success("Status bijgewerkt");
      setStatusOpen(false);
      qc.invalidateQueries({ queryKey: ["werknemer", id] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail?.message || "Status-update mislukt"),
  });

  const avgMut = useMutation({
    mutationFn: (body) => adminPost(`/api/admin/werknemers/${id}/avg-anonymise`, body),
    onSuccess: () => {
      toast.success("Werknemer geanonymiseerd");
      navigate("/admin/werknemers", { replace: true });
    },
    onError: (e) => toast.error(e?.response?.data?.detail?.message || "AVG-anonymise mislukt"),
  });

  const opzegMut = useMutation({
    mutationFn: (body) => adminPost(`/api/admin/werknemers/${id}/opzeggen`, body),
    onSuccess: () => {
      toast.success("Werknemer opgezegd");
      setOpzegOpen(false);
      qc.invalidateQueries({ queryKey: ["werknemer", id] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail?.message || "Opzeggen mislukt"),
  });

  const w = q.data || {};
  const naam = [w.voorletters, w.tussenvoegsel, w.achternaam].filter(Boolean).join(" ");

  return (
    <div data-testid="werknemer-detail-page">
      <PageHeader
        title={naam || "Werknemer"}
        subtitle={w.klantnummer ? `Klantnummer ${w.klantnummer}` : undefined}
        actions={
          <div className="flex flex-wrap gap-2">
            {w.status !== "opgezegd" && w.status !== "geanonymiseerd" ? (
              <button
                type="button"
                onClick={() => setOpzegOpen(true)}
                data-testid="btn-opzeggen"
                className="rounded-md border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-800 hover:bg-amber-100"
              >
                Opzeggen
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => setAvgOpen(true)}
              data-testid="btn-avg-anonymise"
              className="rounded-md bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-rose-700"
            >
              AVG-anonymiseren
            </button>
          </div>
        }
      />

      <ErrorBanner error={q.error} />

      {q.isLoading ? (
        <div data-testid="werknemer-detail-loading" className="text-sm text-slate-500">Laden…</div>
      ) : (
        <div className="space-y-4">
          {/* De hele bovenste rij in één klik weg (Steven, 24-08-2026: "ik wil met 1 knop
              deze hele rij kunnen inklappen... de hele breedte moet in 1 klik in te klappen
              zijn").
              *
              * Waarom als geheel en niet per blok: de drie staan naast elkaar in één raster,
              * en de hoogte van zo'n rij is de hoogte van de langste kolom. Eén blok
              * dichtklappen levert dus geen enkele ruimte op — pas als ze alle drie weg zijn,
              * schuift de rest van de pagina omhoog.
              *
              * Standaard open, want dit is wat je als eerste wilt zien als je een deelnemer
              * opent. */}
          <details open data-testid="rij-bovenaan">
            <summary
              className="uitklapkop mb-2 flex cursor-pointer items-center gap-2 rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:border-teal-600"
              data-testid="rij-bovenaan-knop"
            >
              <Pijl />
              <span>Profiel, bestellen en vragenlijsten</span>
              <span className="ml-auto text-xs font-normal text-slate-500">in- en uitklappen</span>
            </summary>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            {/* Eén blok met alles wat je van deze persoon moet weten.
              *
              * Stond eerst verdeeld over Profiel en een compleet blok Werkgever. Steven,
              * 24-08-2026: "er is een heel blok voor de werkgever gereserveerd, wat ik een
              * beetje onzin vind... je wilt in één oogopslag de belangrijkste gegevens van
              * deze persoon zien." De werkgever staat er nu als één klikbare regel bij; het
              * vrijgekomen blok is voor het bestellen.
              *
              * Adres en geboortedatum staan er bewust bij: als je later moet nakijken waar
              * de zolen heen hadden gemoeten, wil je dat hier vinden en niet in een ander
              * scherm. */}
            {/* Het profiel zelf is geen aparte uitklapper meer: de hele rij gaat in één
                keer dicht (zie hierboven). Twee uitklappers in elkaar zou betekenen dat je
                twee keer moet klikken voor hetzelfde resultaat. */}
            <div className="rounded-md border border-slate-200 bg-white p-4 shadow-sm" data-testid="blok-profiel">
              <div className="mb-3 flex items-center justify-between gap-2">
                <h2 className="text-sm font-semibold text-slate-700">Profiel</h2>
                <button
                  type="button"
                  onClick={() => setBewerkOpen(true)}
                  data-testid="btn-werknemer-bewerken"
                  className="knop-compact rounded border border-slate-300 bg-slate-50 px-2 py-1 text-xs font-medium text-slate-700 hover:border-teal-600"
                >
                  Bewerken
                </button>
              </div>
              <dl className="space-y-1 text-sm">
                <Row label="Geboortedatum">{w.geboortedatum ? formatDatumKort(w.geboortedatum) : "—"}</Row>
                <Row label="E-mail">
                  {w.email ? (
                    <a href={`mailto:${w.email}`} className="text-blue-600 hover:underline">{w.email}</a>
                  ) : "—"}
                  {w.email && w.email_geldig === false ? (
                    <OnbruikbaarMerkje testid="email-onbruikbaar" />
                  ) : null}
                </Row>
                <Row label="Telefoon">
                  {w.telefoon ? (
                    <a href={`tel:${w.telefoon}`} className="text-blue-600 hover:underline">{w.telefoon}</a>
                  ) : "—"}
                </Row>
                <Row label="Adres">
                  <span data-testid="profiel-adres">{adresRegels(w)}</span>
                </Row>
                <Row label="Werkgever">
                  {w.werkgever_id ? (
                    <Link
                      to={`/admin/werkgevers/${w.werkgever_id}`}
                      data-testid="link-werkgever"
                      className="text-blue-600 hover:underline"
                    >
                      {w.werkgever_naam || "Werkgever openen"}
                    </Link>
                  ) : (
                    w.werkgever_naam || "—"
                  )}
                </Row>
                <Row label="Status">
                  <div className="flex items-center gap-2">
                    <StatusBadge value={w.status} testid="status-current" />
                    <button
                      type="button"
                      onClick={() => setStatusOpen(true)}
                      data-testid="btn-status-wijzigen"
                      className="text-xs text-blue-600 hover:underline"
                    >
                      Wijzigen
                    </button>
                  </div>
                </Row>
                <Row label="Aangemaakt">{formatDate(w.created_at)}</Row>
              </dl>
              <UitnodigingBlok werknemer={w} id={id} />
              <ToestemmingBlok werknemer={w} id={id} />
            </div>

            <ZolenBestellen
              werknemer={w}
              bestelling={w.bestelling}
              onBesteld={() => q.refetch()}
            />

            <div className="rounded-md border border-slate-200 bg-white p-4 shadow-sm">
              <h2 className="mb-3 text-sm font-semibold text-slate-700">Vragenlijsten</h2>
              <VragenlijstRegels
                links={w.vragenlijst_links}
                baselineOp={w.baseline_ingevuld_op || w.baseline_voltooid_op}
                followups={w.followup_antwoorden_per_moment || {}}
                leverdatum={w.bestelling?.daadwerkelijke_leverdatum}
              />
              <div className="mt-4 border-t border-slate-100 pt-3 text-xs text-slate-500">
                <div className="flex flex-col gap-1">
                  <Link to={`/admin/pijn-flags?werknemer_id=${id}`} data-testid="link-pijn-flags" className="text-blue-600 hover:underline">Bekijk pijn-signalen →</Link>
                  <Link to={`/admin/bestellingen?werknemer_id=${id}`} data-testid="link-bestellingen" className="text-blue-600 hover:underline">Bekijk bestellingen →</Link>
                </div>
              </div>
            </div>
            </div>
          </details>

          <VragenlijstVergelijking
            baseline={w.baseline_antwoorden}
            baselineOp={w.baseline_ingevuld_op || w.baseline_voltooid_op}
            followups={w.followup_antwoorden_per_moment || {}}
          />

          <SignalenOverzicht werknemerId={id} />

          <BestellingenVanDeelnemer werknemerId={id} />

          <NotitieVeld entiteitType="werknemer" entiteitId={id} />
        </div>
      )}

      {bewerkOpen ? (
        <WerknemerBewerkenModal
          werknemer={w}
          onClose={() => setBewerkOpen(false)}
          onSubmit={(body) => bewerkMut.mutate(body)}
          loading={bewerkMut.isPending}
        />
      ) : null}

      {statusOpen ? (
        <StatusOverrideModal
          klantnummer={w.klantnummer}
          currentStatus={w.status}
          onClose={() => setStatusOpen(false)}
          onSubmit={(body) => statusMut.mutate(body)}
          loading={statusMut.isPending}
        />
      ) : null}
      {avgOpen ? (
        <AvgAnonymiseModal
          naam={naam}
          onClose={() => setAvgOpen(false)}
          onSubmit={(body) => avgMut.mutate(body)}
          loading={avgMut.isPending}
        />
      ) : null}
      {opzegOpen ? (
        <OpzegModal
          naam={naam}
          onClose={() => setOpzegOpen(false)}
          onSubmit={(body) => opzegMut.mutate(body)}
          loading={opzegMut.isPending}
        />
      ) : null}
    </div>
  );
}

function OpzegModal({ naam, onClose, onSubmit, loading }) {
  const [reden, setReden] = useState("");
  const valid = reden.trim().length >= 5;
  return (
    <div role="dialog" aria-modal="true" className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 px-4" data-testid="modal-opzeggen">
      <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-lg">
        <h3 className="text-base font-semibold text-slate-900">Werknemer opzeggen</h3>
        <p className="mt-1 text-xs text-slate-600">{naam} wordt opgezegd. Status wordt &lsquo;opgezegd&rsquo;. Data blijft bewaard tot AVG-anonymisering.</p>
        <label className="mt-4 block text-sm">
          <span className="font-medium text-slate-700">Reden van opzeggen (min 5 tekens)</span>
          <textarea
            value={reden}
            onChange={(e) => setReden(e.target.value)}
            rows={3}
            data-testid="modal-opzeg-reden"
            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            placeholder="Bijv. uit dienst per ..., heeft zelf opgezegd, ..."
          />
        </label>
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700">Annuleren</button>
          <button type="button" disabled={!valid || loading}
            onClick={() => onSubmit({ reden: reden.trim() })}
            data-testid="modal-opzeg-submit"
            className="rounded-md bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-700 disabled:opacity-60">
            {loading ? "Opzeggen…" : "Opzeggen"}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Het driehoekje voor een uitklapkop. Zie base.css voor waarom het niet het standaard is. */
function Pijl() {
  return <span className="uitklap-pijl" aria-hidden="true">&#9656;</span>;
}

function Row({ label, children }) {
  return (
    <div className="grid grid-cols-3 gap-2">
      <dt className="text-xs uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="col-span-2 text-slate-800">{children}</dd>
    </div>
  );
}

/* ── Wat betekent een hogere score? ───────────────────────────────────────────────
 *
 * Dit is geen detail: kleur je het verkeerd om, dan lijkt achteruitgang vooruitgang.
 * Bron is de tabel "Hogere score =" in research/07-vragenlijst-onderbouwing.md, en het
 * komt overeen met wat de signaal-engine doet (regel R5 in signaal_engine.py):
 *
 *   Pijn voet (NRS)            hoger = slechter      -> lager is beter
 *   Bewegen (PROMIS)           hoger = meer moeite   -> lager is beter
 *   Werkvermogen (WAS)         hoger = beter
 *   Werkfunctioneren (WRFQ-5)  hoger = beter (minder moeite)
 *   Werkbeleving (DSPS-6)      hoger = beter
 */
const BETER_IS = {
  pijn_voet: "lager",
  bewegen_huishouden: "lager",
  bewegen_traplopen: "lager",
  bewegen_wandelen: "lager",
  bewegen_boodschappen: "lager",
  werkvermogen: "hoger",
  werk_start: "hoger",
  werk_fouten: "hoger",
  werk_bewegingen: "hoger",
  werk_concentratie: "hoger",
  werk_multitasken: "hoger",
  werkbeleving_stress: "hoger",
  werkbeleving_taken: "hoger",
  werkbeleving_plezier: "hoger",
  werkbeleving_zelfvertrouwen: "hoger",
  werkbeleving_concentratie: "hoger",
  werkbeleving_energie: "hoger",
};

/* Gegevens over de persoon zelf. Die veranderen niet tussen de metingen, dus die krijgen
   geen kolommen naast zich — anders staan er drie keer dezelfde lege vakjes. */
const PERSOONSVELDEN = {
  leeftijd: "Leeftijd",
  geslacht: "Geslacht",
  lengte_cm: "Lengte (cm)",
  gewicht_kg: "Gewicht (kg)",
  functie_sector: "Functie / sector",
  uur_staan_lopen: "Uren staan/lopen per dag",
  type_werkschoen: "Type werkschoen",
  voet_klachten_voorgeschiedenis: "Voorgeschiedenis voetklachten",
};

/* De vragen die wel gemeten worden, gegroepeerd zoals de vragenlijst ze stelt. */
const MEETBLOKKEN = [
  { kop: "Klachten", velden: { pijn_voet: "Pijn voet (0-10)" } },
  {
    kop: "Bewegen",
    velden: {
      bewegen_huishouden: "Huishouden (1-5)",
      bewegen_traplopen: "Traplopen (1-5)",
      bewegen_wandelen: "Wandelen (1-5)",
      bewegen_boodschappen: "Boodschappen (1-5)",
    },
  },
  {
    kop: "Werk",
    velden: {
      werkvermogen: "Werkvermogen (0-10)",
      werk_start: "Direct aan het werk (1-5)",
      werk_fouten: "Werken zonder fouten (1-5)",
      werk_bewegingen: "Bewegingen op het werk (1-5)",
      werk_concentratie: "Concentratie (1-5)",
      werk_multitasken: "Meerdere dingen tegelijk (1-5)",
    },
  },
  {
    kop: "Werkbeleving",
    velden: {
      werkbeleving_stress: "Stress (1-5)",
      werkbeleving_taken: "Taken (1-5)",
      werkbeleving_plezier: "Plezier (1-5)",
      werkbeleving_zelfvertrouwen: "Zelfvertrouwen (1-5)",
      werkbeleving_concentratie: "Concentratie (1-5)",
      werkbeleving_energie: "Energie (1-5)",
    },
  },
];

const MOMENTEN = [
  { sleutel: "baseline", kort: "Nulmeting" },
  { sleutel: "week_4", kort: "Wencontrole wk 4" },
  { sleutel: "week_12", kort: "Vervolgmeting wk 12" },
];

function pijnToon(key, waarde) {
  if (key !== "pijn_voet" || typeof waarde !== "number") return "";
  if (waarde >= 7) return "bg-rose-100 text-rose-800 font-semibold";
  if (waarde >= 4) return "bg-amber-100 text-amber-800 font-semibold";
  return "bg-emerald-100 text-emerald-800 font-semibold";
}

/** Het verschil tussen nulmeting en eindmeting, met een oordeel erbij. */
function berekenVerschil(key, start, eind) {
  if (typeof start !== "number" || typeof eind !== "number") return null;
  const delta = eind - start;
  if (delta === 0) return { tekst: "gelijk", klasse: "text-slate-400" };
  const tekst = `${delta > 0 ? "+" : ""}${delta}`;
  const richting = BETER_IS[key];
  if (!richting) return { tekst, klasse: "text-slate-500" };
  const beter = richting === "lager" ? delta < 0 : delta > 0;
  return { tekst, klasse: beter ? "text-emerald-700 font-semibold" : "text-rose-700 font-semibold" };
}

/* ── De drie vragenlijst-regels met hun link ───────────────────────────────────── */

/* Eén opmaak voor de kopieer- en de openknop. De ene is een <button>, de andere een <a>;
   die renderen van zichzelf verschillend — een link is inline en krijgt geen hoogte mee.
   Met inline-flex, een vaste hoogte en dezelfde minimumbreedte staan ze gelijk. De breedte
   is ruim genoeg voor "Gekopieerd", zodat de knop niet verspringt als je erop klikt. */
/* Eén maat voor de twee knoppen onder een vragenlijst-link: "Kopieer link" en "Openen".
   `knop-compact` hoort erbij — zonder die klasse wordt de <button> op een aanraakscherm
   44px hoog en de <a> niet, en staan ze scheef naast elkaar (zie base.css). */
const LINKKNOP =
  "knop-compact inline-flex h-8 min-w-[7.5rem] items-center justify-center rounded "
  + "border border-slate-300 bg-slate-50 px-3 text-xs font-medium text-slate-700 "
  + "no-underline hover:border-teal-600";

function KopieerKnop({ waarde, testid }) {
  const [gekopieerd, setGekopieerd] = useState(false);
  if (!waarde) return null;
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(waarde);
          setGekopieerd(true);
          setTimeout(() => setGekopieerd(false), 1800);
        } catch {
          toast.error("Kopieren lukte niet — selecteer de link en kopieer met Ctrl+C.");
        }
      }}
      className={LINKKNOP}
    >
      {gekopieerd ? "Gekopieerd" : "Kopieer link"}
    </button>
  );
}

/** Vier weken na levering, respectievelijk twaalf. Zonder levering is er nog geen datum. */
function bedoeldVanaf(leverdatum, weken) {
  if (!leverdatum) return null;
  const d = new Date(leverdatum);
  if (Number.isNaN(d.getTime())) return null;
  d.setDate(d.getDate() + weken * 7);
  return d;
}

function VragenlijstRegels({ links, baselineOp, followups, leverdatum }) {
  const regels = [
    {
      // De gegevenspagina staat bovenaan omdat het de eerste stap van de deelnemer is:
      // gegevens aanvullen, toestemming geven, en dán pas de nulmeting.
      //
      // `ingevuld: "altijd bruikbaar"` en `vanaf: null` is geen sierlijkheid maar
      // noodzaak: de regel hieronder valt terug op "nog niet ingevuld" zodra beide leeg
      // zijn, en dat klopt niet voor een link die altijd werkt.
      //
      // Zolang mailen uitstaat is dit de enige manier om iemand op zijn gegevenspagina
      // te krijgen; zonder deze regel kan de pilot niet draaien.
      naam: "Gegevens aanvullen",
      link: links?.gegevens,
      ingevuld: "altijd bruikbaar",
      vanaf: null,
      testid: "vl-gegevens",
    },
    {
      naam: "Nulmeting",
      link: links?.baseline,
      ingevuld: baselineOp ? formatDate(baselineOp) : null,
      vanaf: null,
      testid: "vl-baseline",
    },
    {
      naam: "Zolen-wencontrole (wk 4)",
      link: links?.week_4,
      ingevuld: followups?.week_4 ? "Ingevuld" : null,
      vanaf: bedoeldVanaf(leverdatum, 4),
      testid: "vl-week4",
    },
    {
      naam: "Vervolgmeting (wk 12)",
      link: links?.week_12,
      ingevuld: followups?.week_12 ? "Ingevuld" : null,
      vanaf: bedoeldVanaf(leverdatum, 12),
      testid: "vl-week12",
    },
  ];

  return (
    <ul className="space-y-2 text-sm" data-testid="werknemer-responses">
      {regels.map((r) => (
        <li key={r.naam} className="border-b border-slate-100 pb-2 last:border-b-0 last:pb-0" data-testid={r.testid}>
          <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
            <span className="font-medium text-slate-900">{r.naam}</span>
            <span className={r.ingevuld ? "text-xs text-slate-600" : "text-xs text-slate-400"}>
              {r.ingevuld
                || (r.vanaf ? `nog niet ingevuld · bedoeld vanaf ${r.vanaf.toLocaleDateString("nl-NL")}` : "nog niet ingevuld")}
            </span>
          </div>
          {r.link ? (
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <span className="truncate font-mono text-[11px] text-slate-400" title={r.link}>{r.link}</span>
              <KopieerKnop waarde={r.link} testid={`${r.testid}-kopieer`} />
              <a
                href={r.link}
                target="_blank"
                rel="noreferrer"
                data-testid={`${r.testid}-openen`}
                className={LINKKNOP}
              >
                Openen
              </a>
            </div>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

/* ── De antwoorden, drie kolommen naast elkaar ─────────────────────────────────── */

function VragenlijstVergelijking({ baseline, baselineOp, followups }) {
  const antwoordenPer = {
    baseline: baseline || null,
    week_4: followups?.week_4 || null,
    week_12: followups?.week_12 || null,
  };
  const aantalMetingen = MOMENTEN.filter((m) => antwoordenPer[m.sleutel]).length;
  if (aantalMetingen === 0) return null;

  const datums = { baseline: baselineOp ? formatDate(baselineOp) : null };

  return (
    <details
      className="rounded-md border border-slate-200 bg-white p-4 shadow-sm"
      data-testid="vragenlijst-scores"
      open={aantalMetingen > 1}
    >
      <summary className="uitklapkop flex cursor-pointer items-center gap-2 text-sm font-semibold text-slate-700">
        <Pijl />
        Vragenlijst-antwoorden
        <span className="ml-2 text-xs font-normal text-slate-500">({aantalMetingen} van 3 ingevuld)</span>
      </summary>

      {baseline ? (
        /* Label boven, waarde eronder, alles op één linkerlijn.
         *
         * Stond eerst als label-links / waarde-rechts. Dat leest netjes zolang elk antwoord
         * één kort woord is, maar zodra er "timmerman in de bouw" of "ja deze zijn ondertussen
         * al 8 maanden aanwezig" in staat, loopt de tekst om en wordt hij naar rechts geduwd:
         * elke regel begint dan op een andere plek. Steven, 24-08-2026: "ik vind deze
         * uitlijning niet nice." */
        <dl
          className="mt-4 grid grid-cols-1 gap-x-8 gap-y-3 text-xs sm:grid-cols-2 lg:grid-cols-4"
          data-testid="persoonsgegevens"
        >
          {Object.entries(PERSOONSVELDEN).map(([key, label]) => {
            const waarde = baseline?.[key];
            if (waarde == null || waarde === "") return null;
            return (
              <div key={key} className="border-b border-slate-100 pb-1.5">
                <dt className="text-[10px] uppercase tracking-wide text-slate-500">{label}</dt>
                <dd className="mt-0.5 text-slate-800">{String(waarde)}</dd>
              </div>
            );
          })}
        </dl>
      ) : null}

      <div className="mt-4 overflow-x-auto">
        <table className="min-w-full text-xs" data-testid="vergelijkingstabel">
          <thead>
            <tr className="border-b border-slate-200 text-[11px] uppercase tracking-wide text-slate-500">
              <th className="py-1.5 pr-3 text-left font-semibold">Vraag</th>
              {MOMENTEN.map((m) => (
                <th key={m.sleutel} className="px-3 py-1.5 text-right font-semibold">
                  {m.kort}
                  <span className="block text-[10px] font-normal normal-case text-slate-400">
                    {datums[m.sleutel] || (antwoordenPer[m.sleutel] ? "ingevuld" : "nog niet ingevuld")}
                  </span>
                </th>
              ))}
              <th className="px-3 py-1.5 text-right font-semibold">
                Verschil
                <span className="block text-[10px] font-normal normal-case text-slate-400">wk 12 t.o.v. start</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {MEETBLOKKEN.map((blok) => {
              const rijen = Object.entries(blok.velden).filter(([key]) =>
                MOMENTEN.some((m) => antwoordenPer[m.sleutel]?.[key] != null)
              );
              if (rijen.length === 0) return null;
              return (
                <React.Fragment key={blok.kop}>
                  <tr>
                    <td colSpan={5} className="pt-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                      {blok.kop}
                    </td>
                  </tr>
                  {rijen.map(([key, label]) => {
                    const verschil = berekenVerschil(
                      key,
                      antwoordenPer.baseline?.[key],
                      antwoordenPer.week_12?.[key]
                    );
                    return (
                      <tr key={key} className="border-b border-slate-50" data-testid={`rij-${key}`}>
                        <td className="py-1 pr-3 text-slate-600">{label}</td>
                        {MOMENTEN.map((m) => {
                          const waarde = antwoordenPer[m.sleutel]?.[key];
                          const leeg = waarde == null || waarde === "";
                          return (
                            <td
                              key={m.sleutel}
                              className={`px-3 py-1 text-right tabular-nums ${antwoordenPer[m.sleutel] ? "" : "bg-slate-50"}`}
                            >
                              {leeg ? (
                                <span className="text-slate-300">—</span>
                              ) : (
                                <span className={`inline-block rounded px-1.5 py-0.5 ${pijnToon(key, waarde)}`}>
                                  {String(waarde)}
                                </span>
                              )}
                            </td>
                          );
                        })}
                        <td className="px-3 py-1 text-right tabular-nums">
                          {verschil ? (
                            <span className={verschil.klasse}>{verschil.tekst}</span>
                          ) : (
                            <span className="text-slate-300">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="mt-3 text-[11px] text-slate-500">
        Groen is vooruitgang, rood achteruitgang. Bij pijn en bewegen betekent een lager cijfer beter; bij
        werkvermogen en werkfunctioneren juist een hoger cijfer.
      </p>
    </details>
  );
}

/**
 * De gegevens van een werknemer bijwerken.
 *
 * Naam en geboortedatum staan er bewust niet bij: daar draait de controle op dubbele
 * aanmeldingen op, en de server accepteert ze ook niet. Klopt een naam niet, dan is dat
 * geen bewerking maar een nieuwe persoon.
 *
 * Het adres stond tot 22-08-2026 nergens op dit scherm, terwijl de zolen er wel heen
 * moeten. Het staat nu in dit venster, zodat het in elk geval te zien en te wijzigen is.
 */
/**
 * De maat opnemen en meteen bestellen.
 *
 * Voor een deelnemer die langskomt in plaats van op een geplande aanmeetdag — meestal een
 * zzp'er. De gewone weg loopt via het tablet op de aanmeetdag; die eist een scan en een
 * adrescontrole. Die zijn hier niet gebeurd, en dat wordt ook niet gedaan alsof.
 */
/** Geboortedatum en dergelijke: 17-05-1990, zoals je het opschrijft. */
function formatDatumKort(waarde) {
  if (!waarde) return "—";
  const d = new Date(waarde);
  if (Number.isNaN(d.getTime())) return String(waarde);
  return d.toLocaleDateString("nl-NL", { day: "2-digit", month: "2-digit", year: "numeric" });
}

/**
 * Het adres als één leesbare regel.
 *
 * Ontbreekt er iets, dan staat dat er ook: een half adres ziet er compleet uit en dat is
 * precies hoe een zending op de verkeerde plek belandt.
 */
function adresRegels(w) {
  const straat = [w.straat, w.huisnummer].filter(Boolean).join(" ");
  const metToevoeging = w.huisnummer_toevoeging ? `${straat}${w.huisnummer_toevoeging}` : straat;
  const plaats = [w.postcode, w.plaats].filter(Boolean).join(" ");
  const delen = [metToevoeging, plaats].filter(Boolean);
  if (delen.length === 0) return "Adres ontbreekt";
  if (!metToevoeging || !plaats) return `${delen.join(", ")} (onvolledig)`;
  return delen.join(", ");
}

/**
 * De aanmeldmail versturen, met een eerlijke stand (blok 3, §3.4).
 *
 * Alle zes de standen worden afgevangen — óók `geopend`, anders verdwijnt het vinkje
 * zodra de deelnemer de mail opent. De faalreden komt als kale `fout_reden`-string binnen
 * en gaat via `uitnodigingRedenNetjes()` naar gewone taal (§3.5). Vóór het versturen een
 * bevestigingsvenster dat het e-mailadres toont: één verkeerde letter geeft een vreemde
 * anders toegang tot de vragenlijst van deze deelnemer.
 */
function beschrijfUitnodiging(stand) {
  switch (stand?.status) {
    case "pending":
      return { tekst: "Klaargezet — gaat weg zodra mailen aanstaat", knop: null };
    case "sending":
      return { tekst: "Wordt nu verstuurd", knop: null };
    case "verzonden":
    case "geopend":
      return { tekst: `✓ Verstuurd op ${formatDatumKort(stand.verzonden_op)}`, knop: "Opnieuw versturen" };
    case "gefaald":
      return { tekst: `Niet gelukt: ${uitnodigingRedenNetjes(stand.fout_reden)}`, knop: "Opnieuw proberen" };
    case "overgeslagen":
      return { tekst: uitnodigingRedenNetjes(stand.fout_reden), knop: "Opnieuw proberen" };
    default:
      // "geen" — er is nog geen bericht
      return { tekst: null, knop: "Aanmeldmail versturen" };
  }
}

function UitnodigingBlok({ werknemer, id }) {
  const qc = useQueryClient();
  const [bevestig, setBevestig] = useState(false);
  const [foutmelding, setFoutmelding] = useState(null);

  const email = werknemer?.email;
  const heeftEmail = !!email;
  const stand = werknemer?.uitnodiging || { status: "geen" };
  const info = beschrijfUitnodiging(stand);

  const mut = useMutation({
    mutationFn: () => adminPost(`/api/admin/werknemers/${id}/uitnodiging`),
    onSuccess: () => {
      setBevestig(false);
      setFoutmelding(null);
      toast.success("Aanmeldmail klaargezet");
      qc.invalidateQueries({ queryKey: ["werknemer", id] });
    },
    onError: (e) => {
      setBevestig(false);
      const detail = e?.response?.data?.detail;
      const code = detail?.error_code;
      if (code === "geen_emailadres") {
        // API-niveau; bewust een aparte melding, níet de overgeslagen-fout_reden.
        setFoutmelding("Geen e-mailadres bekend");
      } else if (code === "uitnodiging_bestaat_al") {
        // Structureel pad: §3.4 toont een resend-knop bij verzonden/geopend, terwijl
        // §3.1 die stand blokkeert. Netjes tonen en de verse stand ophalen.
        const d = detail?.datum ? formatDatumKort(detail.datum) : null;
        setFoutmelding(d ? `Al verstuurd op ${d}` : "Al verstuurd");
        qc.invalidateQueries({ queryKey: ["werknemer", id] });
      } else {
        setFoutmelding(formatApiError(e));
      }
    },
  });

  return (
    <div data-testid="uitnodiging-blok" className="mt-3 border-t border-slate-100 pt-3">
      <div className="text-xs uppercase tracking-wide text-slate-500">Uitnodiging</div>
      <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-slate-800">
        {info.tekst ? (
          <span data-testid="uitnodiging-stand">{info.tekst}</span>
        ) : null}
        {!heeftEmail ? (
          <span data-testid="uitnodiging-geen-email" className="text-xs text-slate-500">
            Geen e-mailadres bekend
          </span>
        ) : info.knop ? (
          <button
            type="button"
            data-testid="btn-uitnodiging-versturen"
            disabled={mut.isPending}
            onClick={() => { setFoutmelding(null); setBevestig(true); }}
            className="knop-compact rounded border border-teal-700 bg-teal-50 px-2.5 py-1 text-xs font-medium text-teal-800 hover:bg-teal-100 disabled:opacity-50"
          >
            {info.knop}
          </button>
        ) : null}
      </div>
      {foutmelding ? (
        <p data-testid="uitnodiging-foutmelding" className="mt-1 text-xs text-rose-700">{foutmelding}</p>
      ) : null}

      {bevestig ? (
        <UitnodigingBevestiging
          email={email}
          bezig={mut.isPending}
          onAnnuleer={() => setBevestig(false)}
          onBevestig={() => mut.mutate()}
        />
      ) : null}
    </div>
  );
}

/**
 * De AVG-toestemming: wie gaf hem, en wanneer (§4.9).
 *
 * Steven, 25-08-2026, op de vraag of een beheerder de toestemming mag vastleggen namens
 * iemand die mondeling of op papier akkoord gaf: *"ja dat mag"*. Dan hoort er wél te
 * staan wie dat deed — anders is de registratie achteraf niet te verantwoorden.
 *
 * Staat er al een toestemming, dan verdwijnt de knop. De server weigert overschrijven
 * (409) omdat een bestaande registratie van de deelnemer zélf kan zijn, en die omzetten
 * naar "beheerder" gooit precies weg wat er vastgelegd moest worden.
 */
function beschrijfToestemming(werknemer) {
  if (!werknemer?.avg_toestemming_op) return null;
  const datum = formatDatumKort(werknemer.avg_toestemming_op);
  if (werknemer.avg_toestemming_bron === "beheerder") {
    const naam = werknemer.avg_toestemming_door_naam;
    return naam
      ? `Vastgelegd door ${naam} op ${datum}`
      : `Vastgelegd door een beheerder op ${datum}`;
  }
  if (werknemer.avg_toestemming_bron === "zelf") {
    return `Door de deelnemer zelf gegeven op ${datum}`;
  }
  // Deelnemers van vóór migratie 059 hebben wel een datum maar geen bron. Daar valt
  // achteraf niets over te zeggen, dus verzinnen we er ook niets bij.
  return `Gegeven op ${datum}`;
}

function ToestemmingBlok({ werknemer, id }) {
  const qc = useQueryClient();
  const [bevestig, setBevestig] = useState(false);
  const [foutmelding, setFoutmelding] = useState(null);

  const tekst = beschrijfToestemming(werknemer);

  const mut = useMutation({
    mutationFn: () => adminPost(`/api/admin/werknemers/${id}/toestemming`, { bevestiging: true }),
    onSuccess: () => {
      setBevestig(false);
      setFoutmelding(null);
      toast.success("Toestemming vastgelegd");
      qc.invalidateQueries({ queryKey: ["werknemer", id] });
    },
    onError: (e) => {
      setBevestig(false);
      const detail = e?.response?.data?.detail;
      if (detail?.error_code === "toestemming_al_vastgelegd") {
        setFoutmelding("De toestemming was al vastgelegd");
        qc.invalidateQueries({ queryKey: ["werknemer", id] });
      } else {
        setFoutmelding(formatApiError(e));
      }
    },
  });

  return (
    <div data-testid="toestemming-blok" className="mt-3 border-t border-slate-100 pt-3">
      <div className="text-xs uppercase tracking-wide text-slate-500">Toestemming</div>
      <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-slate-800">
        {tekst ? (
          <span data-testid="toestemming-stand">{tekst}</span>
        ) : (
          <>
            <span data-testid="toestemming-geen" className="text-xs text-slate-500">
              Nog geen toestemming — deze deelnemer kan de vragenlijst nog niet invullen
            </span>
            <button
              type="button"
              data-testid="btn-toestemming-vastleggen"
              disabled={mut.isPending}
              onClick={() => { setFoutmelding(null); setBevestig(true); }}
              className="knop-compact rounded border border-teal-700 bg-teal-50 px-2.5 py-1 text-xs font-medium text-teal-800 hover:bg-teal-100 disabled:opacity-50"
            >
              Toestemming vastleggen
            </button>
          </>
        )}
      </div>
      {foutmelding ? (
        <p data-testid="toestemming-foutmelding" className="mt-1 text-xs text-rose-700">{foutmelding}</p>
      ) : null}

      {bevestig ? (
        <ToestemmingBevestiging
          bezig={mut.isPending}
          onAnnuleer={() => setBevestig(false)}
          onBevestig={() => mut.mutate()}
        />
      ) : null}
    </div>
  );
}

/**
 * "Heeft u de toestemming echt gekregen?"
 *
 * Een AVG-toestemming vastleggen namens iemand anders is niet terug te draaien vanuit
 * dit scherm — de server weigert overschrijven. Daarom eerst deze vraag, in de woorden
 * waar de beheerder ja op zegt.
 */
function ToestemmingBevestiging({ bezig, onAnnuleer, onBevestig }) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Toestemming vastleggen"
      data-testid="modal-toestemming-bevestigen"
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 px-4"
    >
      <div className="w-full max-w-sm rounded-lg bg-white p-5 shadow-lg">
        <h3 className="text-base font-semibold text-slate-900">Toestemming vastleggen?</h3>
        <p className="mt-2 text-sm text-slate-600">
          Ik heb van deze persoon toestemming gekregen om zijn gegevens te verwerken —
          mondeling of op papier.
        </p>
        <p className="mt-2 text-xs text-slate-500">
          Uw naam en het tijdstip worden erbij vastgelegd. Dit is daarna niet meer vanuit
          dit scherm te wijzigen.
        </p>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onAnnuleer}
            disabled={bezig}
            data-testid="toestemming-bevestig-annuleren"
            className="rounded-md border border-slate-300 px-3 py-1.5 text-sm min-h-[40px]"
          >
            Annuleren
          </button>
          <button
            type="button"
            onClick={onBevestig}
            disabled={bezig}
            data-testid="toestemming-bevestig-oke"
            className="rounded-md bg-teal-700 px-4 py-1.5 text-sm font-medium text-white hover:bg-teal-800 disabled:opacity-50 min-h-[40px]"
          >
            {bezig ? "Bezig…" : "Vastleggen"}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * "Gaat de aanmeldmail hierheen?"
 *
 * Toont het e-mailadres vóór het versturen (§3.4). Eén verkeerd getypte letter geeft een
 * vreemde anders toegang tot de vragenlijst van deze deelnemer.
 */
function UitnodigingBevestiging({ email, bezig, onAnnuleer, onBevestig }) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Aanmeldmail versturen"
      data-testid="modal-uitnodiging-bevestigen"
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 px-4"
    >
      <div className="w-full max-w-sm rounded-lg bg-white p-5 shadow-lg">
        <h3 className="text-base font-semibold text-slate-900">Aanmeldmail versturen?</h3>
        <p className="mt-2 text-sm text-slate-600">De mail wordt klaargezet voor dit adres:</p>
        <p data-testid="uitnodiging-bevestig-email" className="mt-1 text-sm font-semibold text-slate-900">
          {email}
        </p>
        <p className="mt-2 text-xs text-slate-500">
          Controleer het adres: één verkeerde letter geeft iemand anders toegang tot de
          vragenlijst van deze deelnemer.
        </p>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onAnnuleer}
            disabled={bezig}
            data-testid="uitnodiging-bevestig-annuleren"
            className="rounded-md border border-slate-300 px-3 py-1.5 text-sm min-h-[40px]"
          >
            Annuleren
          </button>
          <button
            type="button"
            onClick={onBevestig}
            disabled={bezig}
            data-testid="uitnodiging-bevestig-oke"
            className="rounded-md bg-teal-700 px-4 py-1.5 text-sm font-medium text-white hover:bg-teal-800 disabled:opacity-50 min-h-[40px]"
          >
            {bezig ? "Bezig…" : "Versturen"}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Zolen bestellen, rechtstreeks vanaf de werknemerpagina.
 *
 * Twee knoppen en geen keuzelijst: de zoolsoort komt uit de knop waarop je drukt. Twee zolen
 * die naar verschillende leveranciers gaan, mogen niet van één vergeten vinkje afhangen.
 *
 * Het formulier staat ALTIJD open, ook als er al een bestelling loopt. Steven, 24-08-2026:
 * "ik houd het proces intern wanneer wat wel of niet besteld mag worden, ik wil niet dat
 * daar een beperking komt van de app." Wat er al loopt staat er wel bij, als informatie.
 *
 * De vergissing wordt afgevangen met een bevestigingsvraag vlak voor het versturen, en niet
 * met een slot op de knop.
 */
function ZolenBestellen({ werknemer, bestelling, onBesteld }) {
  // `bestelling` wordt alleen nog gebruikt om de maat voor te vullen; de samenvatting
  // van wat er al besteld is staat in het eigen blok Bestellingen onderaan de pagina.
  const [maat, setMaat] = useState(
    bestelling?.maatschoen_eu != null ? String(bestelling.maatschoen_eu).replace(".", ",") : "",
  );
  const [prefabAantal, setPrefabAantal] = useState(1);
  const [customAantal, setCustomAantal] = useState(1);
  const [opmerking, setOpmerking] = useState("");
  const [getoond, setGetoond] = useState(false);
  const [bevestig, setBevestig] = useState(null);

  const maatGetal = Number(String(maat).replace(",", "."));
  const maatFout = !String(maat).trim()
    ? "Vul de schoenmaat in."
    : !Number.isFinite(maatGetal) || maatGetal < 15 || maatGetal > 52
      ? "Een schoenmaat ligt tussen 15 en 52."
      : null;

  const mut = useMutation({
    mutationFn: (body) => adminPost(`/api/admin/werknemers/${werknemer.werknemer_id}/bestel-direct`, body),
    onSuccess: (data) => {
      toast.success(data?.zoolsoort === "custom" ? "Custom zool besteld" : "Prefab zool besteld");
      setOpmerking("");
      setBevestig(null);
      onBesteld?.();
    },
    onError: (e) => {
      toast.error(e?.response?.data?.detail?.message || "Bestellen mislukt");
      setBevestig(null);
    },
  });

  function vraagBevestiging(zoolsoort, aantal) {
    setGetoond(true);
    if (maatFout) return;
    setBevestig({ zoolsoort, aantal });
  }

  function versturen() {
    mut.mutate({
      maatschoen_eu: maatGetal,
      profiel: "standaard",
      zoolsoort: bevestig.zoolsoort,
      aantal_paar: bevestig.aantal,
      opmerking: opmerking.trim() || null,
    });
  }

  return (
    <div className="rounded-md border border-slate-200 bg-white p-4 shadow-sm" data-testid="blok-zolen-bestellen">
      <h2 className="mb-3 text-sm font-semibold text-slate-700">Zolen bestellen</h2>

      <div className="space-y-4">
        <label className="block">
          <span className="text-xs font-semibold uppercase text-slate-700">Schoenmaat (EU)</span>
          <input
            type="text"
            inputMode="decimal"
            value={maat}
            onChange={(e) => setMaat(e.target.value)}
            placeholder="44"
            data-testid="bestel-maat"
            aria-invalid={getoond && maatFout ? true : undefined}
            className="mt-1 block w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm min-h-[40px]"
          />
          {getoond && maatFout ? (
            <span data-testid="bestel-fout-maat" className="mt-1 block text-xs text-rose-700">{maatFout}</span>
          ) : null}
        </label>

        <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
          <p className="text-xs font-semibold uppercase text-slate-700">Prefab zool</p>
          <div className="mt-2 flex items-end gap-2">
            <label className="flex-1">
              <span className="block text-xs text-slate-600">Aantal paar</span>
              <select
                value={prefabAantal}
                onChange={(e) => setPrefabAantal(Number(e.target.value))}
                data-testid="prefab-aantal"
                className="mt-1 block w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm min-h-[40px]"
              >
                {[1, 2, 3, 4].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
            <button
              type="button"
              onClick={() => vraagBevestiging("prefab", prefabAantal)}
              disabled={mut.isPending}
              data-testid="btn-bestel-prefab"
              className="rounded-md bg-teal-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-teal-800 disabled:opacity-50 min-h-[40px]"
            >
              Bestel
            </button>
          </div>
        </div>

        <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
          <p className="text-xs font-semibold uppercase text-slate-700">Custom zool (PLT)</p>
          <div className="mt-2 flex items-end gap-2">
            <label className="flex-1">
              <span className="block text-xs text-slate-600">Aantal paar</span>
              <select
                value={customAantal}
                onChange={(e) => setCustomAantal(Number(e.target.value))}
                data-testid="custom-aantal"
                className="mt-1 block w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm min-h-[40px]"
              >
                {[1, 2, 3, 4].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
            <button
              type="button"
              onClick={() => vraagBevestiging("custom", customAantal)}
              disabled={mut.isPending}
              data-testid="btn-bestel-custom"
              className="rounded-md border border-teal-700 px-3 py-1.5 text-sm font-medium text-teal-800 hover:bg-teal-50 disabled:opacity-50 min-h-[40px]"
            >
              Bestel
            </button>
          </div>
        </div>

        {/* Inklapbaar: de meeste bestellingen hebben geen bijzonderheid, en dan hoort er
            geen leeg tekstvak de helft van het blok te vullen. Staat er iets in, dan blijft
            hij open zodat je hem niet ongemerkt kwijtraakt. */}
        <details className="rounded-md border border-slate-200 p-3" open={opmerking.length > 0}>
          <summary className="uitklapkop flex cursor-pointer items-center gap-2 text-xs font-semibold uppercase text-slate-700" data-testid="opmerking-openen">
            <Pijl />
            Opmerking bij de bestelling
            {opmerking.length > 0 ? (
              <span className="ml-2 font-normal normal-case text-slate-500">(ingevuld)</span>
            ) : null}
          </summary>
          <textarea
            value={opmerking}
            onChange={(e) => setOpmerking(e.target.value.slice(0, 500))}
            rows={3}
            placeholder="Bijvoorbeeld: extra afdeklaag leer"
            data-testid="bestel-opmerking"
            className="mt-2 block w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm"
          />
        </details>
      </div>

      {bevestig ? (
        <BestelBevestiging
          werknemer={werknemer}
          zoolsoort={bevestig.zoolsoort}
          aantal={bevestig.aantal}
          maat={maat}
          opmerking={opmerking.trim()}
          bezig={mut.isPending}
          onAnnuleer={() => setBevestig(null)}
          onBevestig={versturen}
        />
      ) : null}
    </div>
  );
}

/**
 * "Mag deze bestelling doorgevoerd worden?"
 *
 * De enige rem op het bestellen (Steven, 24-08-2026). De app blokkeert niets meer; dit venster
 * laat zien wát er de deur uit gaat, zodat een misklik zichtbaar wordt vóór hij vertrekt.
 */
function BestelBevestiging({ werknemer, zoolsoort, aantal, maat, opmerking, bezig, onAnnuleer, onBevestig }) {
  const naam = [werknemer.voorletters, werknemer.achternaam].filter(Boolean).join(" ");
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Bestelling bevestigen"
      data-testid="modal-bestel-bevestigen"
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 px-4"
    >
      <div className="w-full max-w-sm rounded-lg bg-white p-5 shadow-lg">
        <h3 className="text-base font-semibold text-slate-900">Bestelling doorvoeren?</h3>
        <dl className="mt-3 space-y-1 text-sm" data-testid="bevestig-samenvatting">
          <Row label="Voor">{naam || werknemer.klantnummer || "deze deelnemer"}</Row>
          <Row label="Soort">{zoolsoort === "custom" ? "Custom zool (PLT)" : "Prefab zool"}</Row>
          <Row label="Aantal">{aantal} paar</Row>
          <Row label="Schoenmaat">{maat}</Row>
          {opmerking ? <Row label="Opmerking">{opmerking}</Row> : null}
        </dl>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onAnnuleer}
            disabled={bezig}
            data-testid="bevestig-annuleren"
            className="rounded-md border border-slate-300 px-3 py-1.5 text-sm min-h-[40px]"
          >
            Annuleren
          </button>
          <button
            type="button"
            onClick={onBevestig}
            disabled={bezig}
            data-testid="bevestig-oke"
            className="rounded-md bg-teal-700 px-4 py-1.5 text-sm font-medium text-white hover:bg-teal-800 disabled:opacity-50 min-h-[40px]"
          >
            {bezig ? "Bezig…" : "Oké"}
          </button>
        </div>
      </div>
    </div>
  );
}

function BewerkVeld({ naam, label, type, placeholder, waarde, onChange, fout }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold uppercase text-slate-700">{label}</span>
      <input
        type={type || "text"}
        value={waarde}
        onChange={(e) => onChange(naam, e.target.value)}
        placeholder={placeholder}
        data-testid={`bewerk-${naam}`}
        aria-invalid={fout ? true : undefined}
        className="mt-1 block w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm min-h-[40px]"
      />
      {fout ? (
        <span data-testid={`bewerk-fout-${naam}`} className="mt-1 block text-xs text-rose-700">{fout}</span>
      ) : null}
    </label>
  );
}

function WerknemerBewerkenModal({ werknemer, onClose, onSubmit, loading }) {
  const [form, setForm] = useState({
    email: werknemer?.email || "",
    telefoon: werknemer?.telefoon || "",
    straat: werknemer?.straat || "",
    huisnummer: werknemer?.huisnummer || "",
    huisnummer_toevoeging: werknemer?.huisnummer_toevoeging || "",
    postcode: werknemer?.postcode || "",
    plaats: werknemer?.plaats || "",
  });
  const [getoond, setGetoond] = useState(false);

  function set(veld, waarde) {
    setForm((f) => ({ ...f, [veld]: waarde }));
  }

  const fouten = {};
  if (form.email.trim() && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) {
    fouten.email = "Vul een geldig e-mailadres in.";
  }
  if (form.telefoon.trim() && !/^\+?[\d\s-]{8,15}$/.test(form.telefoon.trim())) {
    fouten.telefoon = "Een telefoonnummer heeft 8 tot 15 cijfers.";
  }
  if (form.postcode.trim() && !/^\d{4}\s?[A-Za-z]{2}$/.test(form.postcode.trim())) {
    fouten.postcode = "Een postcode ziet eruit als 1234 AB.";
  }
  const compleet = Object.keys(fouten).length === 0;

  /* Alleen wat écht veranderd is gaat mee. De server weigert een leeg verzoek, en een
     veld dat je niet hebt aangeraakt hoort niet opnieuw weggeschreven te worden. */
  const gewijzigd = Object.entries(form).filter(
    ([veld, waarde]) => waarde.trim() !== (werknemer?.[veld] || "")
  );

  function versturen() {
    setGetoond(true);
    if (!compleet || gewijzigd.length === 0) return;
    const body = {};
    gewijzigd.forEach(([veld, waarde]) => {
      body[veld] = waarde.trim() === "" ? null : waarde.trim();
    });
    onSubmit(body);
  }

  const veld = (naam, label, extra = {}) => (
    <BewerkVeld
      naam={naam}
      label={label}
      type={extra.type}
      placeholder={extra.placeholder}
      waarde={form[naam]}
      onChange={set}
      fout={getoond ? fouten[naam] : null}
    />
  );

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Gegevens bewerken"
      data-testid="modal-werknemer-bewerken"
      className="fixed inset-0 z-40 flex items-center justify-center overflow-y-auto bg-black/40 px-4 py-8"
    >
      <div className="my-auto w-full max-w-lg rounded-lg bg-white p-5 shadow-lg">
        <h3 className="text-base font-semibold text-slate-900">Gegevens bewerken</h3>
        <p className="mt-1 text-xs text-slate-500">
          {werknemer?.voorletters} {werknemer?.achternaam} · {werknemer?.klantnummer}
        </p>
        <p className="mt-3 text-xs text-slate-600">
          Naam en geboortedatum staan vast: daar herkennen wij een dubbele aanmelding aan.
        </p>

        <div className="mt-4 grid gap-3">
          {veld("email", "E-mailadres", { type: "email", placeholder: "naam@bedrijf.nl" })}
          {veld("telefoon", "Telefoonnummer", { placeholder: "0612345678" })}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="col-span-2">{veld("straat", "Straat", { placeholder: "Dorpsstraat" })}</div>
            {veld("huisnummer", "Nummer", { placeholder: "12" })}
            {veld("huisnummer_toevoeging", "Toev.", { placeholder: "A" })}
          </div>
          <div className="grid grid-cols-2 gap-3">
            {veld("postcode", "Postcode", { placeholder: "1234 AB" })}
            {veld("plaats", "Plaats", { placeholder: "Amersfoort" })}
          </div>
        </div>

        {getoond && gewijzigd.length === 0 ? (
          <p data-testid="bewerk-niets-gewijzigd" className="mt-3 text-xs text-slate-600">
            Er is niets gewijzigd.
          </p>
        ) : null}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            data-testid="bewerk-annuleren"
            className="rounded-md border border-slate-300 px-3 py-1.5 text-sm min-h-[40px]"
          >
            Annuleren
          </button>
          <button
            type="button"
            onClick={versturen}
            disabled={loading}
            data-testid="bewerk-opslaan"
            className="rounded-md bg-teal-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-teal-800 disabled:opacity-50 min-h-[40px]"
          >
            {loading ? "Bezig…" : "Opslaan"}
          </button>
        </div>
      </div>
    </div>
  );
}

function StatusOverrideModal({ klantnummer, currentStatus, onClose, onSubmit, loading }) {
  const [status, setStatus] = useState(currentStatus || "");
  const [reden, setReden] = useState("");
  const valid = status && status !== currentStatus && reden.trim().length >= 5;

  return (
    <div role="dialog" aria-modal="true" className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 px-4" data-testid="modal-status">
      <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-lg">
        <h3 className="text-base font-semibold text-slate-900">Status wijzigen</h3>
        <p className="mt-1 text-xs text-slate-500">{klantnummer}</p>
        <label className="mt-4 block text-sm">
          <span className="font-medium text-slate-700">Nieuwe status</span>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            aria-label="Status select" data-testid="modal-status-select"
            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
          >
            <option value="">— Kies —</option>
            {WERKNEMER_STATUSES.map((s) => <option key={s} value={s}>{WERKNEMER_STATUS_LABELS[s] || s}</option>)}
          </select>
        </label>
        <label className="mt-3 block text-sm">
          <span className="font-medium text-slate-700">Reden (verplicht, min 5 chars)</span>
          <textarea
            value={reden}
            onChange={(e) => setReden(e.target.value)}
            rows={3}
            data-testid="modal-status-reden"
            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
          />
        </label>
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} data-testid="modal-status-cancel"
            className="rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50">
            Annuleren
          </button>
          <button
            type="button"
            disabled={!valid || loading}
            onClick={() => onSubmit({ status, reden })}
            data-testid="modal-status-submit"
            className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
          >
            {loading ? "Bezig…" : "Bevestig"}
          </button>
        </div>
      </div>
    </div>
  );
}

function AvgAnonymiseModal({ naam, onClose, onSubmit, loading }) {
  const [reden, setReden] = useState("");
  const [confirmNaam, setConfirmNaam] = useState("");
  const validReden = reden.trim().length >= 5;
  const validNaam = confirmNaam.trim() === (naam || "").trim() && naam;
  const valid = validReden && validNaam;

  return (
    <div role="dialog" aria-modal="true" className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 px-4" data-testid="modal-avg">
      <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-lg">
        <h3 className="text-base font-semibold text-rose-700">⚠ AVG-anonymise</h3>
        <p className="mt-2 text-xs text-slate-600">
          Deze actie verwijdert alle persoonsgegevens van <strong>{naam}</strong> permanent.
          Antwoorden blijven beschikbaar voor geaggregeerde rapportage.
          Deze actie is <strong>onomkeerbaar</strong>.
        </p>
        <label className="mt-4 block text-sm">
          <span className="font-medium text-slate-700">Reden (min 5 chars)</span>
          <textarea
            value={reden}
            onChange={(e) => setReden(e.target.value)}
            rows={3}
            data-testid="modal-avg-reden"
            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
          />
        </label>
        <label className="mt-3 block text-sm">
          <span className="font-medium text-slate-700">Type "<em>{naam}</em>" om te bevestigen</span>
          <input
            type="text"
            value={confirmNaam}
            onChange={(e) => setConfirmNaam(e.target.value)}
            data-testid="modal-avg-naam"
            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
          />
        </label>
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} data-testid="modal-avg-cancel"
            className="rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50">
            Annuleren
          </button>
          <button
            type="button"
            disabled={!valid || loading}
            onClick={() => onSubmit({ reden })}
            data-testid="modal-avg-submit"
            className="rounded-md bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-rose-700 disabled:opacity-60"
          >
            {loading ? "Bezig…" : "Definitief anonymiseren"}
          </button>
        </div>
      </div>
    </div>
  );
}


function SignalenOverzicht({ werknemerId }) {
  const q = useQuery({
    queryKey: ["werknemer-signalen", werknemerId],
    queryFn: () => adminGet(`/api/admin/pijn-flags?werknemer_id=${werknemerId}&limit=100`),
    enabled: !!werknemerId,
  });
  /* Vooruitgang staat niet als eigen regel tussen de signalen, maar als slotzin onder
     het signaal dat over hetzelfde onderwerp ging (Steven, 25-08-2026). Zelfde regel als
     op de pijn-signalenpagina, uit dezelfde functie — anders lopen de twee schermen weer
     uit elkaar. */
  const items = splitsVerbeteringen(q.data?.items || []).signalen;
  const open = items.filter((f) => !f.afgehandeld_op);
  const afgehandeld = items.filter((f) => !!f.afgehandeld_op);

  const aantal = items.length;

  return (
    /* Inklapbaar, en standaard DICHT (Steven, 24-08-2026: "verder moeten de pijnsignalen
     * ingeklapt staan"). Het aantal open signalen staat wel in de kop, zodat je ziet dat er
     * iets is zonder dat het de pagina vult. */
    <details
      className="rounded-md border border-slate-200 bg-white p-4 shadow-sm"
      data-testid="werknemer-signalen-tab"
    >
      <summary className="uitklapkop flex cursor-pointer items-center gap-2">
        <Pijl />
        {/* De teller volgt de database, niet de lijst die eronder staat: is er een
            signaal overgeslagen, dan zegt de kop het echte aantal en legt de melding
            eronder uit waarom er eentje ontbreekt. */}
        <span className="text-sm font-semibold text-slate-700">
          Pijn-signalen alert ({aantal})
          {open.length > 0 ? (
            <span className="ml-2 text-xs font-normal text-amber-700">{open.length} open</span>
          ) : null}
        </span>
        <Link
          to={`/admin/pijn-flags?werknemer_id=${werknemerId}`}
          onClick={(e) => e.stopPropagation()}
          className="ml-auto text-xs text-blue-600 hover:underline"
        >
          Volledige lijst →
        </Link>
      </summary>
      {/* Een pijnsignaal dat er niet meer bij staat, is niet te onderscheiden van een
          signaal dat er nooit was — daarom hoort dit hier hard gemeld te worden. */}
      <OvergeslagenBanner
        aantal={q.data?.overgeslagen} enkelvoud="pijnsignaal" meervoud="pijnsignalen"
        testid="werknemer-signalen-overgeslagen"
      />
      {q.isLoading ? (
        <p className="mt-2 text-xs text-slate-500">Laden…</p>
      ) : items.length === 0 ? (
        <p className="mt-2 text-xs text-slate-500">Geen pijn-signalen voor deze deelnemer.</p>
      ) : (
        <div className="mt-3 space-y-3">
          {open.length > 0 && (
            <div>
              <div className="text-xs font-medium text-amber-700">Open ({open.length})</div>
              <ul className="mt-1 divide-y divide-slate-100">
                {open.map((f) => <SignaalRij key={f.flag_id} f={f} />)}
              </ul>
            </div>
          )}
          {afgehandeld.length > 0 && (
            <details className="text-xs">
              <summary className="uitklapkop flex cursor-pointer items-center gap-2 font-medium text-emerald-700">
                <Pijl />Afgehandeld ({afgehandeld.length})
              </summary>
              <ul className="mt-1 divide-y divide-slate-100">
                {afgehandeld.map((f) => <SignaalRij key={f.flag_id} f={f} />)}
              </ul>
            </details>
          )}
        </div>
      )}
    </details>
  );
}

/**
 * Wat er voor deze deelnemer besteld is, en wanneer.
 *
 * Steven, 24-08-2026: "ik wil een rij onder pijnsignalen van bestellingen zodat je kan zien
 * wanneer wat besteld is voor deze klant." Sinds er meerdere bestellingen naast elkaar mogen
 * bestaan, is één regel bovenin het bestelblok te weinig — de geschiedenis hoort compleet.
 *
 * Inklapbaar en standaard dicht, net als de signalen.
 */
function BestellingenVanDeelnemer({ werknemerId }) {
  const q = useQuery({
    queryKey: ["werknemer-bestellingen", werknemerId],
    queryFn: () => adminGet(`/api/admin/bestellingen?werknemer_id=${werknemerId}&limit=50`),
    enabled: !!werknemerId,
  });
  const items = q.data?.items || [];

  return (
    <details
      className="rounded-md border border-slate-200 bg-white p-4 shadow-sm"
      data-testid="werknemer-bestellingen"
    >
      <summary className="uitklapkop flex cursor-pointer items-center gap-2">
        <Pijl />
        <span className="text-sm font-semibold text-slate-700">
          Bestellingen ({q.data?.total ?? items.length})
        </span>
        <Link
          to={`/admin/bestellingen?werknemer_id=${werknemerId}`}
          onClick={(e) => e.stopPropagation()}
          className="ml-auto text-xs text-blue-600 hover:underline"
        >
          Volledige lijst →
        </Link>
      </summary>

      {q.isLoading ? (
        <p className="mt-2 text-xs text-slate-500">Laden…</p>
      ) : items.length === 0 ? (
        <p className="mt-2 text-xs text-slate-500" data-testid="geen-bestellingen">
          Er is nog niets besteld voor deze deelnemer.
        </p>
      ) : (
        <div className="mt-3 overflow-x-auto">
          <table className="min-w-full text-xs" data-testid="bestellingen-tabel">
            <thead>
              <tr className="border-b border-slate-200 text-[11px] uppercase tracking-wide text-slate-500">
                <th className="py-1.5 pr-3 text-left font-semibold">Besteld op</th>
                <th className="px-3 py-1.5 text-left font-semibold">Soort</th>
                <th className="px-3 py-1.5 text-left font-semibold">Maat</th>
                <th className="px-3 py-1.5 text-left font-semibold">Paar</th>
                <th className="px-3 py-1.5 text-left font-semibold">Opmerking</th>
                <th className="px-3 py-1.5 text-left font-semibold">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((b) => (
                <tr key={b.bestelling_id} data-testid={`bestelregel-${b.bestelling_id}`}>
                  <td className="py-1.5 pr-3 text-slate-700">
                    {b.besteld_op ? formatDate(b.besteld_op) : <span className="text-slate-400">nog niet besteld</span>}
                  </td>
                  <td className="px-3 py-1.5">{b.zoolsoort === "custom" ? "Custom (PLT)" : "Prefab"}</td>
                  <td className="px-3 py-1.5 tabular-nums">{b.maatschoen_eu ?? "—"}</td>
                  <td className="px-3 py-1.5 tabular-nums">{b.aantal_paar ?? 1}</td>
                  <td className="px-3 py-1.5 text-slate-600">{b.opmerking || "—"}</td>
                  <td className="px-3 py-1.5"><StatusBadge value={b.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </details>
  );
}

/**
 * Eén alert, uitklapbaar.
 *
 * Steven, 24-08-2026: "ik snap hier de ballen van... ik zou graag willen dat de R-rij
 * aanklikbaar is zodat er een rij eronder opent met een beknopte uitleg wat het betekent en
 * wat er specifiek is ingevuld."
 *
 * Dichtgeklapt staat er dus een gewone Nederlandse zin. Uitgeklapt komt daaronder wat het
 * betekent, waar de grens vandaan komt, wat de deelnemer invulde en wanneer het gemeten is.
 * De regelcode staat alleen nog in de titel-tekst, voor wie moet uitzoeken waaróm iets
 * gebeurde (ADR-065).
 */
function SignaalRij({ f }) {
  const code = f.regel_id || "";
  const naam = signaalZin(f);
  const uitleg = SIGNAAL_UITLEG[code];
  const fase = FASE_NAMEN[f.fase] || null;
  // Hoe hard dit om aandacht vraagt, als lichte tint op de rij (besluit 24-08-2026).
  const niveau = signaalNiveau(f);

  return (
    <li
      className={`niveau-${niveau} rounded-sm py-1 pl-2 text-xs`}
      data-testid={`signaal-${f.flag_id}`}
      data-niveau={niveau}
      title={NIVEAU_UITLEG[niveau]}
    >
      <details>
        <summary
          className="uitklapkop flex cursor-pointer items-center gap-2 py-0.5"
          title={code || undefined}
        >
          <Pijl />
          <span className="font-medium text-slate-900">{naam}</span>
          {f.notificatie_categorie === "direct" && (
            <span className="rounded bg-red-100 px-1.5 py-0.5 text-red-800">Meteen nakijken</span>
          )}
          <span className="ml-auto whitespace-nowrap text-slate-500">{formatDate(f.doorgestuurd_op)}</span>
        </summary>

        <div className="mt-1 space-y-1 border-l-2 border-slate-100 pl-3 text-slate-600">
          {uitleg ? (
            <p className="whitespace-pre-line" data-testid={`signaal-${f.flag_id}-uitleg`}>
              {perTweeZinnen(uitleg)}
            </p>
          ) : f.omschrijving ? (
            <p className="whitespace-pre-line">{perTweeZinnen(f.omschrijving)}</p>
          ) : null}
          {f.verbetering_zin ? (
            <p className="text-emerald-700" data-testid={`signaal-${f.flag_id}-verbetering`}>
              {f.verbetering_zin}
            </p>
          ) : null}
          {f.gemeten_waarde != null ? (
            <p className="text-slate-500" data-testid={`signaal-${f.flag_id}-ingevuld`}>
              Ingevuld: <span className="text-slate-700">{f.gemeten_waarde}</span>
              {f.drempel != null ? ` — het signaal gaat af vanaf ${f.drempel}.` : ""}
            </p>
          ) : null}
          {fase ? <p className="text-slate-500">{fase}.</p> : null}
        </div>
      </details>
    </li>
  );
}
